import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { EASE } from "../Reveal";

export default function Hero({ home }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 130]);
  const lines = [home.hero_line1, home.hero_line2, home.hero_line3].filter(Boolean);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen bg-cream overflow-hidden flex items-center">
      <div className="absolute -left-48 top-1/4 w-[34rem] h-[34rem] rounded-full bg-blush blur-3xl opacity-80 pointer-events-none" />
      <div className="absolute right-10 bottom-10 w-80 h-80 rounded-full bg-petal/40 blur-3xl pointer-events-none" />
      <span className="hidden xl:block absolute left-8 top-1/2 -translate-y-1/2 -rotate-90 origin-left text-[10px] uppercase tracking-[0.5em] text-ink/30">
        Est. 2011 · Mumbai
      </span>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-24 grid lg:grid-cols-12 gap-12 items-center relative w-full">
        <div className="lg:col-span-7 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className="flex flex-wrap gap-3"
            data-testid="hero-eyebrow"
          >
            <span className="rounded-full bg-ink text-cream px-4 py-2 text-[9px] font-bold uppercase tracking-[0.2em]">
              {home.hero_eyebrow}
            </span>
            <span className="rounded-full bg-petal text-ink px-4 py-2 text-[9px] font-bold uppercase tracking-[0.2em]">
              3Bn+ Views
            </span>
          </motion.div>

          <h1 className="mt-7 font-display uppercase text-[13.5vw] sm:text-6xl lg:text-7xl xl:text-[5.6rem] leading-[0.98] text-ink" data-testid="hero-headline">
            {lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 1 ? "text-gold" : ""}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.25 + i * 0.14, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
            className="mt-8 max-w-xl text-base md:text-lg text-neutral-600 leading-relaxed"
            data-testid="hero-subtext"
          >
            {home.hero_subtext}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to="/edit"
              data-testid="hero-cta-edit"
              className="group inline-flex items-center gap-3 bg-ink text-cream rounded-full pl-7 pr-2 py-2 text-xs font-bold uppercase tracking-[0.2em] hover:bg-gold hover:text-ink transition-colors duration-300"
            >
              Explore The Edit
              <span className="w-9 h-9 rounded-full bg-champagne text-ink flex items-center justify-center transition-transform duration-300 group-hover:rotate-45">
                <ArrowUpRight size={15} />
              </span>
            </Link>
            <Link
              to="/edit?category=Real Weddings"
              data-testid="hero-cta-weddings"
              className="group inline-flex items-center gap-3 border-2 border-ink rounded-full pl-7 pr-2 py-2 text-xs font-bold uppercase tracking-[0.2em] hover:bg-blush transition-colors duration-300"
            >
              Real Weddings
              <span className="w-9 h-9 rounded-full bg-ink text-cream flex items-center justify-center transition-transform duration-300 group-hover:rotate-45">
                <ArrowUpRight size={15} />
              </span>
            </Link>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            style={{ y }}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.4, ease: EASE }}
            className="relative"
          >
            <div className="absolute -inset-3 border-2 border-champagne/60 rounded-t-full pointer-events-none" />
            <img
              src={home.hero_image}
              alt="A bride and groom at their wedding ceremony"
              data-testid="hero-image"
              className="w-full aspect-[3/4] object-cover rounded-t-full"
            />
            <div className="absolute -bottom-8 -left-8 w-28 h-28 hidden md:block animate-spin-slow" aria-hidden="true">
              <svg viewBox="0 0 100 100" className="w-full h-full">
                <defs>
                  <path id="tboi-circle" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" />
                </defs>
                <text fontSize="8" letterSpacing="2.4" fill="#B89742" fontFamily="Archivo, sans-serif" fontWeight="700">
                  <textPath href="#tboi-circle">THE BRIDES OF INDIA · SINCE 2011 ·</textPath>
                </text>
              </svg>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-ink/40"
      >
        <span className="text-[9px] uppercase tracking-[0.4em] font-bold">Scroll</span>
        <span className="w-px h-10 bg-gradient-to-b from-gold to-transparent" />
      </motion.div>
    </section>
  );
}
